import { useState, useEffect, useMemo } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Tooltip, Chip,
} from '@mui/material'
import { FancyButton } from '../../comps/FancyButtons'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import LayersIcon from '@mui/icons-material/Layers'
import CfpSelect from '../../comps/CfpSelect'
import { FS_SM, FS_XS, ICON_SM } from '../../common/theme'
import { useApp, useAppProject } from '../../AppContext'

interface TaElementsModalProps {
  open: boolean
  onClose: () => void
  // Drives the title, the source of available items, and where the
  // parent stores the result:
  //   • 'structure' → els  → question.structuretweaks
  //   • 'answer'    → ela  → question.answertweaks
  //   • 'scoring'   → scoring templates from currentProject.scoringtemplates
  //                  → question.bideval.scoringelements
  mode: 'structure' | 'answer' | 'scoring'
  // Kept on the interface for parity with existing call sites in case a
  // future mode wants to filter by question state.
  question?: any
  // Items already attached to the question for this mode. A COPY of this
  // list becomes the modal's internal draft on open - Apply emits the
  // draft, Cancel discards it.
  selected?: any[]
  onApply: (elements: any[]) => void
}

// Group shown first if present - matches the angularjs "Standard" seed.
const DEFAULT_GROUP_NAME = 'Standard'

// Scope filter values used in 'scoring' mode.
const SCOPE_ALL = '__all__'
const SCOPE_GLOBAL = '__global__'
const SCOPE_PROJECT = '__project__'

// Strip HTML for a clean tag name.
function plainText(html?: string | null): string {
  if (!html) return ''
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '')
  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent ?? '').trim()
}

// Map an available tag (from elementGroups) into the slim stored shape.
function toStoredTweak(t: any): { id: string; name: string } {
  return {
    id: t?.tagsha ?? '',
    name: plainText(t?.tagnamehtml) || t?.tagname || plainText(t?.valuehtml) || '(unnamed)',
  }
}

// Map a scoring template into the slim stored shape used by
// question.bideval.scoringelements.
function toStoredScoringElement(t: any): { id: string; name: string } {
  return {
    id: t?.id ?? t?.elementid ?? '',
    name: t?.name ?? '(unnamed)',
  }
}

export default function TaElementsModal({
  open, onClose, mode, selected = [], onApply,
}: TaElementsModalProps) {
  const { elementGroups } = useApp()
  const { currentProject } = useAppProject()
  const isScoring = mode === 'scoring'

  // Working copy. Add/remove operations mutate this; Apply emits it.
  const [draft, setDraft] = useState<any[]>([])
  // Reused as: tweak mode → elementGroups name; scoring mode → scope filter.
  const [groupName, setGroupName] = useState<string>(DEFAULT_GROUP_NAME)
  const [scoringScope, setScoringScope] = useState<string>(SCOPE_ALL)

  const wantType = mode === 'structure' ? 'els' : mode === 'answer' ? 'ela' : ''

  // On open: deep-copy the parent's list into draft + reset the group /
  // scope picker. Run only when `open` flips so the user's in-flight
  // edits aren't blown away on every render.
  useEffect(() => {
    if (!open) return
    setDraft(structuredClone(selected ?? []))
    if (isScoring) {
      setScoringScope(SCOPE_ALL)
      return
    }
    const names = (elementGroups ?? []).map((g: any) => g.name)
    if (names.length === 0) return
    if (names.includes(DEFAULT_GROUP_NAME)) setGroupName(DEFAULT_GROUP_NAME)
    else setGroupName(names[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ─── Tweak modes (structure/answer) - grouped tag picker ────────────────
  const groupOptions = useMemo(() => (
    (elementGroups ?? []).map((g: any) => ({ value: g.name, label: g.name }))
  ), [elementGroups])

  const activeGroup = useMemo(() => (
    (elementGroups ?? []).find((g: any) => g.name === groupName)
  ), [elementGroups, groupName])

  const availableTags = useMemo(() => {
    if (isScoring) return []
    const tags = (activeGroup?.styletags ?? []).filter((t: any) => t.type === wantType)
    return tags.map((t: any) => ({ ...t, _groupname: activeGroup?.name }))
  }, [activeGroup, wantType, isScoring])

  // ─── Scoring mode - flat template list filtered by scope ────────────────
  // The server hands back `currentProject.scoringtemplates` already merged
  // (globals + this project's own). Each entry's optional `projectsha`
  // identifies whether it's global (absent) or attached to a project.
  const scoringTemplates = useMemo(() => (
    (currentProject?.scoringtemplates ?? [])
  ), [currentProject?.scoringtemplates])

  const projectSha = currentProject?.projectsha
  const filteredScoringTemplates = useMemo(() => {
    if (!isScoring) return []
    if (scoringScope === SCOPE_GLOBAL) {
      return scoringTemplates.filter((t: any) => !t.projectsha)
    }
    if (scoringScope === SCOPE_PROJECT) {
      return scoringTemplates.filter((t: any) => t.projectsha && t.projectsha === projectSha)
    }
    return scoringTemplates
  }, [isScoring, scoringScope, scoringTemplates, projectSha])

  const scopeOptions = useMemo(() => [
    { value: SCOPE_ALL, label: 'All', hint: String(scoringTemplates.length) },
    { value: SCOPE_GLOBAL, label: 'Global', hint: String(scoringTemplates.filter((t: any) => !t.projectsha).length) },
    { value: SCOPE_PROJECT, label: 'This project', hint: String(scoringTemplates.filter((t: any) => t.projectsha === projectSha).length) },
  ], [scoringTemplates, projectSha])

  // ─── Draft helpers - single shape ({id,name}) across all modes ──────────
  const tweakId = (t: any) => t?.tagsha
  const tweakInDraft = (t: any) => !!tweakId(t) && draft.some(d => d?.id === tweakId(t))
  const templateId = (t: any) => t?.id ?? t?.elementid
  const templateInDraft = (t: any) => !!templateId(t) && draft.some(d => d?.id === templateId(t))

  const addTweak = (t: any) => {
    setDraft(prev => prev.some(d => d?.id === tweakId(t)) ? prev : [...prev, toStoredTweak(t)])
  }
  const removeTweak = (t: any) => {
    setDraft(prev => prev.filter(d => d?.id !== tweakId(t)))
  }
  const addTemplate = (t: any) => {
    setDraft(prev => prev.some(d => d?.id === templateId(t)) ? prev : [...prev, toStoredScoringElement(t)])
  }
  const removeTemplate = (t: any) => {
    setDraft(prev => prev.filter(d => d?.id !== templateId(t)))
  }
  const toggleTweak = (t: any) => { tweakInDraft(t) ? removeTweak(t) : addTweak(t) }
  const toggleTemplate = (t: any) => { templateInDraft(t) ? removeTemplate(t) : addTemplate(t) }

  const handleApply = () => {
    onApply(draft)
    onClose()
  }

  const title =
    mode === 'structure' ? 'Storyboard Tweaks' :
    mode === 'answer' ? 'Answer Tweaks' :
    'Scoring Templates'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ 'data-testid': 'elem_dlg', 'data-mode': mode } as any}
    >
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LayersIcon sx={{ fontSize: ICON_SM, opacity: 0.7 }} />
        <Typography
          data-testid="elem_title"
          component="span"
          variant="subtitle1"
          sx={{ fontWeight: 600, fontSize: '0.95rem', flex: 1 }}
        >
          {title}
        </Typography>
        <Tooltip title="Close">
          <IconButton data-testid="elem_close_btn" size="small" onClick={onClose} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: ICON_SM }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      {/* Picker row - group for tweaks, scope for scoring. */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
        {isScoring ? (
          <>
            <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7, fontSize: FS_XS }}>
              Scope
            </Typography>
            <CfpSelect
              label=""
              value={scoringScope}
              onChange={setScoringScope}
              options={scopeOptions}
              testId="elem_scope_select"
            />
          </>
        ) : (
          <>
            <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7, fontSize: FS_XS }}>
              Group
            </Typography>
            <CfpSelect
              label=""
              value={groupName}
              onChange={setGroupName}
              options={groupOptions.length > 0 ? groupOptions : [{ value: '', label: 'No groups' }]}
              disabled={groupOptions.length === 0}
            />
          </>
        )}
      </Box>

      <DialogContent dividers sx={{ p: 0 }}>
        {isScoring ? (
          filteredScoringTemplates.length === 0 ? (
            <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.55, fontSize: FS_SM }}>
                No scoring templates available
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {filteredScoringTemplates.map((tpl: any) => {
                const inDraft = templateInDraft(tpl)
                const isGlobal = !tpl.projectsha
                return (
                  <Box
                    key={templateId(tpl) ?? tpl.name}
                    data-testid="elem_tpl_row"
                    data-template-id={templateId(tpl)}
                    data-scope={isGlobal ? 'global' : 'project'}
                    data-selected={inDraft ? 'true' : 'false'}
                    onClick={() => toggleTemplate(tpl)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 2, py: 0.75,
                      cursor: 'pointer',
                      borderBottom: 1, borderColor: 'divider',
                      borderLeft: 4,
                      borderLeftColor: inDraft ? 'primary.main' : 'transparent',
                      bgcolor: inDraft ? 'primary.50' : 'transparent',
                      color: inDraft ? 'primary.main' : 'text.primary',
                      transition: 'background-color 0.12s, border-color 0.12s, color 0.12s',
                      '&:hover': { bgcolor: inDraft ? 'primary.100' : 'action.hover' },
                    }}
                  >
                    <Box sx={{
                      flexShrink: 0,
                      width: 22, height: 22, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: 2,
                      borderColor: inDraft ? 'primary.main' : 'divider',
                      bgcolor: inDraft ? 'primary.main' : 'transparent',
                      color: inDraft ? 'common.white' : 'text.disabled',
                      transition: 'background-color 0.12s, border-color 0.12s, color 0.12s',
                    }}>
                      {inDraft
                        ? <CheckIcon sx={{ fontSize: 14 }} />
                        : <AddIcon sx={{ fontSize: 14 }} />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, fontSize: FS_SM, fontWeight: inDraft ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tpl.name ?? '(unnamed)'}
                    </Box>
                    <Chip
                      size="small"
                      label={isGlobal ? 'Global' : 'Project'}
                      sx={{ height: 18, fontSize: '0.62rem', opacity: 0.75 }}
                    />
                    {inDraft && (
                      <Tooltip title="Remove from selection">
                        <IconButton
                          size="small"
                          tabIndex={-1}
                          onClick={(e) => { e.stopPropagation(); removeTemplate(tpl) }}
                          sx={{ p: 0.25, color: 'primary.main' }}
                        >
                          <CloseIcon sx={{ fontSize: ICON_SM }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )
              })}
            </Box>
          )
        ) : (
          availableTags.length === 0 ? (
            <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.55, fontSize: FS_SM }}>
                No tweaks available
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {availableTags.map((tag: any) => {
                const inDraft = tweakInDraft(tag)
                const labelHtml = tag.tagnamehtml || tag.tagname || tag.valuehtml || '(unnamed)'
                return (
                  <Box
                    key={tag.tagsha ?? `${tag._groupname}-${tag.tagnamehtml ?? tag.tagname ?? ''}`}
                    data-testid="elem_tag_row"
                    data-tag-sha={tag.tagsha}
                    data-group-name={tag._groupname}
                    data-selected={inDraft ? 'true' : 'false'}
                    onClick={() => toggleTweak(tag)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 2, py: 0.75,
                      cursor: 'pointer',
                      borderBottom: 1, borderColor: 'divider',
                      borderLeft: 4,
                      borderLeftColor: inDraft ? 'primary.main' : 'transparent',
                      bgcolor: inDraft ? 'primary.50' : 'transparent',
                      color: inDraft ? 'primary.main' : 'text.primary',
                      transition: 'background-color 0.12s, border-color 0.12s, color 0.12s',
                      '&:hover': { bgcolor: inDraft ? 'primary.100' : 'action.hover' },
                    }}
                  >
                    <Box sx={{
                      flexShrink: 0,
                      width: 22, height: 22, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: 2,
                      borderColor: inDraft ? 'primary.main' : 'divider',
                      bgcolor: inDraft ? 'primary.main' : 'transparent',
                      color: inDraft ? 'common.white' : 'text.disabled',
                      transition: 'background-color 0.12s, border-color 0.12s, color 0.12s',
                    }}>
                      {inDraft
                        ? <CheckIcon sx={{ fontSize: 14 }} />
                        : <AddIcon sx={{ fontSize: 14 }} />}
                    </Box>
                    <Box
                      sx={{ flex: 1, minWidth: 0, fontSize: FS_SM, fontWeight: inDraft ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      dangerouslySetInnerHTML={{ __html: labelHtml }}
                    />
                    {inDraft && (
                      <Tooltip title="Remove from selection">
                        <IconButton
                          size="small"
                          tabIndex={-1}
                          onClick={(e) => { e.stopPropagation(); removeTweak(tag) }}
                          sx={{ p: 0.25, color: 'primary.main' }}
                        >
                          <CloseIcon sx={{ fontSize: ICON_SM }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )
              })}
            </Box>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Typography variant="caption" sx={{ opacity: 0.6, fontSize: FS_XS, flex: 1 }}>
          {draft.length} selected
        </Typography>
        <FancyButton testId="elem_cancel_btn" onClick={onClose}>Cancel</FancyButton>
        <FancyButton
          testId="elem_apply_btn"
          solid
          onClick={handleApply}
        >
          Apply
        </FancyButton>
      </DialogActions>
    </Dialog>
  )
}
